import { TranslateFunc } from 'src/components/translate';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import { Store } from 'src/types';
import {
  IPaymentProvider,
  IPaymentOption,
  IPaymentOptionBase,
  PaymentProviderGroup,
} from './types';

const PAYMENT_GROUP_SETTINGS = [
  { group: PaymentProviderGroup.Invoice, count: 1 },
  { group: PaymentProviderGroup.Direct, count: 1 },
  { group: PaymentProviderGroup.Card, count: 2 },
];

const supportedPaymentOptions: IPaymentOptionBase[] = [
  { name: 'BITCOIN', translationKey: 'store.payment-options-bitcoin' },
  { name: 'CARD', translationKey: 'store.payment-options-card' },
  {
    name: 'CASH_ON_DELIVERY',
    translationKey: 'store.payment-options-cash-on-delivery',
  },
  { name: 'DIRECT', translationKey: 'store.payment-options-direct' },
  { name: 'INSTALLMENT', translationKey: 'store.payment-options-installment' },
  { name: 'INVOICE', translationKey: 'store.payment-options-invoice' },
  { name: 'PHONE_BILL', translationKey: 'store.payment-options-phone-bill' },
  { name: 'PREPAID', translationKey: 'store.payment-options-prepaid' },
  { name: 'SMS', translationKey: 'store.payment-options-sms' },
];

const supportedPaymentProviders = [
  {
    name: 'KLARNA',
    label: 'Klarna',
    logo: 'klarna.svg',
    group: PaymentProviderGroup.Invoice,
  },
  // To be turned on when supported by backend ENUM
  // {
  //   name: 'MOBILE_PAY',
  //   label: 'Mobile pay',
  //   logo: 'mobile-pay.svg',
  // },
  // { name: 'DANKORT', label: 'Dankort', logo: 'dankort.svg', sortOrder: 3 },
  // { name: 'AFTERPAY', label: 'Afterpay', logo: 'afterpay.svg', sortOrder: 4 },
  {
    name: 'SWISH',
    label: 'Swish',
    logo: 'swish.svg',
    group: PaymentProviderGroup.Direct,
  },
  {
    name: 'VIPPS',
    label: 'Vipps',
    logo: 'vipps.svg',
    group: PaymentProviderGroup.Direct,
  },
  {
    name: 'MASTER_CARD',
    label: 'MasterCard',
    logo: 'mastercard.svg',
    group: PaymentProviderGroup.Card,
  },
  {
    name: 'VISA',
    label: 'Visa',
    logo: 'visa.svg',
    group: PaymentProviderGroup.Card,
  },
  {
    name: 'PAYPAL',
    label: 'PayPal',
    logo: 'paypal.svg',
    group: PaymentProviderGroup.Direct,
  },

  {
    name: 'AMEX',
    label: 'American Express',
    logo: 'americanexpress.svg',
    group: PaymentProviderGroup.Card,
  },
  {
    name: 'SVEA_WEB_PAY',
    label: 'SveaWebPay',
    logo: 'svea_ekonomi.svg',
    group: PaymentProviderGroup.Invoice,
  },
  {
    name: 'PAYSON',
    label: 'Payson',
    logo: 'payson.svg',
    group: PaymentProviderGroup.Invoice,
  },
  {
    name: 'DINERS',
    label: 'Diners Club',
    logo: 'diners.svg',
    group: PaymentProviderGroup.Card,
  },
] as const;

export const getPaymentOptions = (
  store: Store,
  t: TranslateFunc
): { hasOptions: boolean; options: IPaymentOption[] } => {
  const activePaymentOptions =
    store.payment?.options?.filter(Boolean).map(option => option?.name) ?? [];
  const hasOptions = activePaymentOptions.length > 0;
  const options = supportedPaymentOptions
    .map(option => ({
      ...option,
      active: activePaymentOptions.includes(option.name),
      translatedName: t('store', option.translationKey),
    }))
    .sort((a, b) => {
      if ((a.active && b.active) || (!a.active && !b.active)) {
        return a.translatedName.localeCompare(b.translatedName);
      }

      if (a.active) {
        return -1;
      }

      return 1;
    });

  return {
    hasOptions,
    options,
  };
};

const compareSortOrder = (a: IPaymentProvider, b: IPaymentProvider) => {
  if (a.sortOrder < 0) {
    return 1;
  }
  if (b.sortOrder < 0) {
    return -1;
  }
  if (a.sortOrder < b.sortOrder) {
    return -1;
  }
  return 0;
};

export const getPaymentProviders = (
  store: Store,
  market: ClientMarket
): { hasProviders: boolean; providers: IPaymentProvider[] } => {
  if (!store) {
    return { hasProviders: false, providers: [] };
  }

  const marketPriorities = market?.paymentProviders?.priorities
    ? market.paymentProviders.priorities
    : [];

  const activePaymentProviders =
    store.payment?.providers?.filter(Boolean).map(provider => provider?.name) ??
    [];

  const hasProviders = activePaymentProviders.length > 0;

  const providers = supportedPaymentProviders?.map(provider => ({
    ...provider,
    sortOrder: marketPriorities.indexOf(provider.name),
    active: activePaymentProviders.includes(provider.name),
    logoUrl: `${market.cdn}/g/rfe/store/paymentprovider/${provider.logo}`,
  }));

  const providersSortedByMarketPrio = [...providers].sort(compareSortOrder);

  return { hasProviders, providers: providersSortedByMarketPrio };
};

export const getGroupedPaymentProviders = (
  store: Store,
  market: ClientMarket
): IPaymentProvider[] => {
  const { providers, hasProviders } = getPaymentProviders(store, market);
  const groupedProviders: (IPaymentProvider | null)[] = [];
  const activeProviders = providers.filter(p => p.active);

  if (hasProviders && market?.paymentProviders?.groupPriorities?.length) {
    market.paymentProviders.groupPriorities.forEach(
      (group: PaymentProviderGroup) => {
        const visibilitySetting = PAYMENT_GROUP_SETTINGS.find(
          setting => setting.group === group
        );
        const visible = visibilitySetting?.count || 0;
        for (let index = 0; index < visible; index += 1) {
          groupedProviders.push(
            getPrioritizedProvidersForGroup(activeProviders, group, index)
          );
        }
      }
    );
  } else if (hasProviders) {
    // Fallback if there are no group priorities for the market
    groupedProviders.push(activeProviders[0]);
  }

  return groupedProviders.filter(n => n) as IPaymentProvider[];
};

const getPrioritizedProvidersForGroup = (
  providers: IPaymentProvider[],
  group: PaymentProviderGroup,
  pos: number
) => {
  const providersFromGroup = providers.filter(p => p.group === group);
  return providersFromGroup.length > pos ? providersFromGroup[pos] : null;
};
